import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import { 
  ClipboardList, 
  Clock, 
  CheckCircle2, 
  User, 
  Calendar,
  Pill,
  Phone,
  ArrowRight,
  RefreshCw,
  AlertCircle,
  Stethoscope
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

export default function CMACheckoutQueuePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState({ pending: 0, in_progress: 0, completed_today: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // active, completed

  const loadQueue = useCallback(async () => {
    try {
      const endpoint = filter === 'completed' 
        ? '/checkout/history/completed'
        : '/checkout/queue';
      
      const res = await api.get(endpoint);
      
      if (filter === 'completed') {
        setQueue(res.data.completed_checkouts || []);
      } else {
        setQueue(res.data.queue || []);
        setStats(res.data.stats || { pending: 0, in_progress: 0, completed_today: 0 });
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading queue:', err);
      }
      toast.error('Failed to load checkout queue');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadQueue();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadQueue, 30000);
    return () => clearInterval(interval);
  }, [loadQueue]);

  const handleStartCheckout = async (checkoutId) => {
    try {
      await api.post(`/checkout/${checkoutId}/start`);
      toast.success('Checkout started');
      navigate(`/cma/checkout/${checkoutId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">Pending</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-300">In Progress</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 border-green-300">Completed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getInstructionSummary = (instructions) => {
    if (!instructions) return [];
    const items = [];
    if (instructions.return_visit) items.push({ icon: Calendar, text: `Return: ${instructions.return_visit}` });
    if (instructions.scheduling_needs) items.push({ icon: Clock, text: instructions.scheduling_needs });
    if (instructions.pharmacy_verification) items.push({ icon: Pill, text: instructions.pharmacy_verification });
    if (instructions.medication_followup) items.push({ icon: Phone, text: instructions.medication_followup });
    return items;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="cma-checkout-queue-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="h-7 w-7 text-teal-600" />
            CMA Checkout Queue
          </h1>
          <p className="text-slate-600 mt-1">
            Complete checkout tasks for patients after provider encounters
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={loadQueue}
          className="flex items-center gap-2"
          data-testid="refresh-queue-btn"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Pending</p>
                <p className="text-3xl font-bold text-yellow-600" data-testid="stat-pending">{stats.pending}</p>
              </div>
              <Clock className="h-10 w-10 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">In Progress</p>
                <p className="text-3xl font-bold text-blue-600" data-testid="stat-in-progress">{stats.in_progress}</p>
              </div>
              <RefreshCw className="h-10 w-10 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Completed Today</p>
                <p className="text-3xl font-bold text-green-600" data-testid="stat-completed-today">{stats.completed_today}</p>
              </div>
              <CheckCircle2 className="h-10 w-10 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 border-b">
        <button
          data-testid="tab-active-queue"
          onClick={() => setFilter('active')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            filter === 'active' 
              ? 'border-teal-600 text-teal-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Active Queue ({stats.pending + stats.in_progress})
        </button>
        <button
          data-testid="tab-completed"
          onClick={() => setFilter('completed')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            filter === 'completed' 
              ? 'border-teal-600 text-teal-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Completed Today
        </button>
      </div>

      {/* Queue List */}
      {queue.length === 0 ? (
        <Card className="p-12 text-center" data-testid="queue-empty-state">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">
            {filter === 'active' ? 'No patients in checkout queue' : 'No completed checkouts today'}
          </h3>
          <p className="text-slate-500 mt-1">
            {filter === 'active' 
              ? 'Patients will appear here when providers complete encounters'
              : 'Completed checkouts will appear here'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {queue.map((item) => (
            <Card 
              key={item.id} 
              data-testid={`checkout-item-${item.id}`}
              className={`hover:shadow-md transition-shadow ${
                item.status === 'in_progress' ? 'ring-2 ring-blue-400' : ''
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Patient & Provider Info */}
                    <div className="flex items-center gap-4 mb-3">
                      <div className="flex items-center gap-2">
                        <User className="h-5 w-5 text-slate-400" />
                        <span className="font-semibold text-lg text-slate-900" data-testid="checkout-item-patient-name">
                          {item.patient_name}
                        </span>
                      </div>
                      {getStatusBadge(item.status)}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-slate-600 mb-3">
                      <div className="flex items-center gap-1">
                        <Stethoscope className="h-4 w-4" />
                        <span>Dr. {item.provider_name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{formatDate(item.created_at)}</span>
                      </div>
                      {item.assigned_cma_name && (
                        <div className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          <span>Assigned: {item.assigned_cma_name}</span>
                        </div>
                      )}
                    </div>

                    {/* Provider Instructions */}
                    {item.instructions && (
                      <div className="bg-slate-50 rounded-lg p-3 mt-2">
                        <p className="text-xs font-medium text-slate-500 uppercase mb-2">
                          Provider Instructions
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {getInstructionSummary(item.instructions).map((instr, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                              <instr.icon className="h-4 w-4 text-teal-600 flex-shrink-0" />
                              <span>{instr.text}</span>
                            </div>
                          ))}
                          {item.instructions.additional_notes && (
                            <div className="col-span-2 flex items-start gap-2 text-sm text-slate-700">
                              <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                              <span>{item.instructions.additional_notes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Completed Info */}
                    {item.status === 'completed' && item.checkout_data && (
                      <div className="bg-green-50 rounded-lg p-3 mt-2">
                        <p className="text-xs font-medium text-green-700 uppercase mb-1">
                          Completed by {item.checkout_data.completed_by_name} at {formatDate(item.completed_at)}
                        </p>
                        {item.checkout_data.scheduled_followup_date && (
                          <p className="text-sm text-green-800">
                            Follow-up scheduled: {item.checkout_data.scheduled_followup_date}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  {item.status !== 'completed' && (
                    <div className="ml-4">
                      {item.status === 'pending' ? (
                        <Button 
                          onClick={() => handleStartCheckout(item.id)}
                          className="bg-teal-600 hover:bg-teal-700"
                          data-testid={`start-checkout-btn-${item.id}`}
                        >
                          Start Checkout
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      ) : item.status === 'in_progress' && item.assigned_cma_id === user?.id ? (
                        <Button 
                          onClick={() => navigate(`/cma/checkout/${item.id}`)}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid={`continue-checkout-btn-${item.id}`}
                        >
                          Continue
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      ) : item.status === 'in_progress' ? (
                        <Badge variant="outline" className="text-blue-600">
                          Assigned to {item.assigned_cma_name}
                        </Badge>
                      ) : null}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
